import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { FilterSelect, FilterSelectItem } from '@/components/ui/filter-select';

/**
 * PaginatedTable - A wrapper component that adds pagination to any table
 *
 * Usage:
 * <PaginatedTable
 *   data={allData}
 *   renderTable={(paginatedData, isLoading) => (
 *     <YourTable data={paginatedData} isLoading={isLoading} />
 *   )}
 * />
 *
 * Or with server-side pagination:
 * <PaginatedTable
 *   loadData={async (page, pageSize) => {
 *     const response = await Entity.listWithTotal(orderBy, pageSize, (page - 1) * pageSize);
 *     return { data: response.data, total: response.total };
 *   }}
 *   renderTable={(data, isLoading) => (
 *     <YourTable data={data} isLoading={isLoading} />
 *   )}
 *   dependencies={[filterValue, searchTerm]} // Re-fetch when these change
 * />
 */
export default function PaginatedTable({
  // Client-side pagination: pass all data
  data,

  // Server-side pagination: pass a load function
  loadData,

  // Dependencies that trigger data reload (for server-side)
  dependencies = [],

  // Render function for the table
  renderTable,

  // Optional: external loading state
  externalLoading = false,

  // Optional: when true, only the FIRST fetch toggles the loading state;
  // subsequent dependency-triggered refetches swap data in quietly (no spinner /
  // no blank rows). Used for live-refresh during a guided run to stop the blink.
  quietRefetch = false,

  // Pagination options
  defaultPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],

  // Optional: wrap in Card
  wrapInCard = false,

  // Optional: className for the container
  className = '',
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [internalData, setInternalData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedOnce = React.useRef(false);

  const isServerSide = typeof loadData === 'function';
  const totalPages = Math.ceil(total / pageSize) || 1;

  // Server-side pagination: load data when page/pageSize/dependencies change
  useEffect(() => {
    if (isServerSide) {
      // Quiet refetch: after the first load, don't blank the table on dependency-
      // triggered refetches (live refresh) — just swap the data in. Stops blinking.
      const showSpinner = !(quietRefetch && hasLoadedOnce.current);
      const fetchData = async () => {
        if (showSpinner) setIsLoading(true);
        try {
          const result = await loadData(page, pageSize);
          setInternalData(result.data || []);
          setTotal(result.total || 0);
          hasLoadedOnce.current = true;
        } catch (error) {
          console.error('Error loading paginated data:', error);
          if (!quietRefetch) { setInternalData([]); setTotal(0); }
        } finally {
          if (showSpinner) setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [page, pageSize, ...dependencies]);

  // Reset to page 1 when dependencies change
  useEffect(() => {
    if (dependencies.length > 0) {
      setPage(1);
    }
  }, dependencies);

  // Client-side pagination: slice data when data/page/pageSize change
  useEffect(() => {
    if (!isServerSide && data) {
      setTotal(data.length);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setInternalData(data.slice(startIndex, endIndex));
    }
  }, [data, page, pageSize, isServerSide]);

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [totalPages]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const startItem = total === 0 ? 0 : ((page - 1) * pageSize) + 1;
  const endItem = Math.min(page * pageSize, total);

  const loading = isLoading || externalLoading;

  const paginationControls = total > 0 && (
    <div className="flex flex-col gap-3 mt-5 items-stretch md:flex-row md:flex-wrap md:items-center md:justify-between">
      {/* Left: page size selector */}
      <div className="flex items-center gap-2 justify-end md:justify-start">
        <FilterSelect
          value={String(pageSize)}
          onValueChange={(v) => handlePageSizeChange(parseInt(v))}
          className="h-6 w-[148px] px-2"
        >
          {pageSizeOptions.map(size => (
            <FilterSelectItem key={size} value={String(size)}>{size} per page</FilterSelectItem>
          ))}
        </FilterSelect>
      </div>

      {/* Right: page navigation — shown only when there's more than one page */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1 justify-center md:justify-start">
          <button onClick={() => handlePageChange(1)} disabled={page === 1}
            className="flex items-center justify-center size-6 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronsLeft size={14} />
          </button>
          <button onClick={() => handlePageChange(Math.max(1, page - 1))} disabled={page === 1}
            className="flex items-center justify-center size-6 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronLeft size={14} />
          </button>
          <span className="text-sm font-normal px-2 text-page-text">
            {page} of {totalPages} pages
          </span>
          <button onClick={() => handlePageChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
            className="flex items-center justify-center size-6 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronRight size={14} />
          </button>
          <button onClick={() => handlePageChange(totalPages)} disabled={page >= totalPages}
            className="flex items-center justify-center size-6 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            <ChevronsRight size={14} />
          </button>
        </div>
      )}
    </div>
  );

  const content = (
    <div className={className}>
      {renderTable(internalData, loading)}
      {paginationControls}
    </div>
  );


  if (wrapInCard) {
    return (
      <div className="bg-card-bg rounded-[5px] overflow-hidden">
        {content}
      </div>
    );
  }

  return content;
}
